import type { Universe } from '@biasafe.models';
import { UNIVERSE_SCOPES } from './pointInTimeData';

// Offline mirror of the backend's UNIVERSE_STATIC_DATA
// (lib/py/biasafe/src/biasafe/providers/morningstar/static_data/universe_static.py),
// which is the source of truth. Serves the catalogue while /universes/data is in
// flight and when it fails.
//
// Values are transcribed VERBATIM and must stay that way: the whole point is
// that the offline card is indistinguishable from the live one, so the index's
// name, coverage, cadence and blurb don't change under the user mid-load. Only
// the MEASURED fields — market cap and the size mix — are left empty here,
// because those genuinely come from ClickHouse.
//
// The rebalancer preset in particular is per-index and is NOT decorative: it
// seeds the step-2 schedule when a universe is picked. A generic default used
// to stand in for all six, so picking US Target Market Exposure before the
// catalogue resolved seeded annual reconstitution with rebalancing off instead
// of quarterly with daily rebalancing on, and Canada Domestic got NYSE instead
// of TSX.
//
// IDs match the backend `VALID_UNIVERSE_IDS`.

interface StaticUniverseDef {
  id: string;
  /** ClickHouse's `portfolio_name` — what the API returns as `name`. */
  name: string;
  officialName: string;
  benchmarkCode: string;
  displayOrder: number;
  coverageLabel: string;
  coverageScope: number;
  /**
   * How many countries the index covers. A published property of the index
   * (Canada Domestic covers Canada: 1), not a measurement — so it renders
   * immediately, then yields to the live count once the securities query
   * resolves. Values verified against ClickHouse at the latest CLS snapshot.
   */
  countryCount: number;
  inception: string;
  assetClass: string;
  assetClassCoverage: number;
  marketSegments: string;
  indexReconFrequency: string;
  indexRebalanceFrequency: string;
  description: string;
  rebalancer: Universe['rebalancerDetails'];
}

const STATIC_DEFS: ReadonlyArray<StaticUniverseDef> = [
  {
    id: '1911833.0',
    name: 'Morningstar US Target Market Exposure',
    officialName: 'Morningstar US Target Market Exposure Index',
    benchmarkCode: 'MSUTMETU',
    displayOrder: 1,
    coverageLabel: 'United States',
    coverageScope: 85.0,
    countryCount: 1,
    inception: 'July 31, 1998',
    assetClass: 'Equities',
    assetClassCoverage: 100.0,
    marketSegments: 'Large- & Mid-Caps',
    indexReconFrequency: 'Semi Annually',
    indexRebalanceFrequency: 'Quarterly',
    description:
      'The Morningstar US Target Market Exposure Index measures the ' +
      'performance of large- and mid-cap stocks in the U.S., representing ' +
      'the top 85% of the investable universe by float-adjusted market ' +
      'capitalization.',
    rebalancer: {
      exchange: 'XNYS',
      holidayRoll: 'previous',
      tieBreak: 'previous',
      reconstitution: {
        reconFrequency: 'Q',
        firstReconMonth: 6,
        weekOfMonth: 3,
        weekday: 'FRI',
        alignToBusinessDay: { activated: false, periodEndMethod: 'first' },
      },
      rebalance: {
        activated: true,
        methodology: 'calendar-based',
        reconFrequency: 'D',
        firstReconMonth: 3,
        weekOfMonth: 3,
        weekday: 'FRI',
        alignToBusinessDay: { activated: false, periodEndMethod: 'first' },
      },
    },
  },
  {
    id: '2499059.0',
    name: 'Morningstar US Market Extended',
    officialName: 'Morningstar US Market Extended Index',
    benchmarkCode: 'MUMEXTGU',
    displayOrder: 2,
    coverageLabel: 'United States',
    coverageScope: 99.5,
    countryCount: 1,
    inception: 'August 29, 1997',
    assetClass: 'Equities',
    assetClassCoverage: 100.0,
    marketSegments: 'All Caps',
    indexReconFrequency: 'Semi Annually',
    indexRebalanceFrequency: 'Quarterly',
    description:
      'The Morningstar US Market Extended Index measures the performance of ' +
      'large-, mid- and small-cap extended stocks in the U.S., representing ' +
      'the top 99.5% of the investable universe by market capitalization.',
    rebalancer: {
      exchange: 'XNYS',
      holidayRoll: 'previous',
      tieBreak: 'previous',
      reconstitution: {
        reconFrequency: 'SA',
        firstReconMonth: 6,
        weekOfMonth: 3,
        weekday: 'FRI',
        alignToBusinessDay: { activated: false, periodEndMethod: 'first' },
      },
      rebalance: {
        activated: true,
        methodology: 'calendar-based',
        reconFrequency: 'Q',
        firstReconMonth: 3,
        weekOfMonth: 3,
        weekday: 'FRI',
        alignToBusinessDay: { activated: false, periodEndMethod: 'first' },
      },
    },
  },
  {
    id: '2575855.0',
    name: 'Morningstar Canada Domestic',
    officialName: 'Morningstar Canada Domestic Index',
    benchmarkCode: 'MSCAUSDG',
    displayOrder: 3,
    coverageLabel: 'Canada',
    coverageScope: 97.0,
    countryCount: 1,
    inception: 'July 31, 1998',
    assetClass: 'Equities',
    assetClassCoverage: 100.0,
    marketSegments: 'All Caps',
    indexReconFrequency: 'Semi Annually',
    indexRebalanceFrequency: 'Quarterly',
    description:
      'The Morningstar Canada Domestic Index measures the performance of ' +
      'large-, mid- and small-cap stocks in Canada, representing the top ' +
      '97% of the investable universe by market capitalization. Eligible ' +
      'stocks are weighted in proportion to domestic float- adjusted market ' +
      'capitalization.',
    rebalancer: {
      exchange: 'XTSE',
      holidayRoll: 'previous',
      tieBreak: 'previous',
      reconstitution: {
        reconFrequency: 'SA',
        firstReconMonth: 6,
        weekOfMonth: 3,
        weekday: 'FRI',
        alignToBusinessDay: { activated: false, periodEndMethod: 'first' },
      },
      rebalance: {
        activated: false,
        methodology: 'calendar-based',
        reconFrequency: 'Q',
        firstReconMonth: 3,
        weekOfMonth: 3,
        weekday: 'FRI',
        alignToBusinessDay: { activated: false, periodEndMethod: 'first' },
      },
    },
  },
  {
    id: 'P000001109',
    name: 'Morningstar Developed Markets ex-North America',
    officialName: 'Morningstar Developed Markets ex-North America Index',
    benchmarkCode: 'MSDIGUS',
    displayOrder: 4,
    coverageLabel: 'Developed Markets (ex-NA)',
    coverageScope: 97.0,
    countryCount: 22,
    inception: 'July 31, 1998',
    assetClass: 'Equities',
    assetClassCoverage: 100.0,
    marketSegments: 'All Caps',
    indexReconFrequency: 'Semi Annually',
    indexRebalanceFrequency: 'Quarterly',
    description:
      'The Morningstar Developed Markets ex-North America Index measures ' +
      'the performance of large-, mid- and small-cap stocks in developed ' +
      'markets around the world outside of North America, representing the ' +
      'top 97% of the investable universe by market capitalization.',
    rebalancer: {
      exchange: 'XNYS',
      holidayRoll: 'following',
      tieBreak: 'previous',
      reconstitution: {
        reconFrequency: 'A',
        firstReconMonth: 6,
        weekOfMonth: 3,
        weekday: 'FRI',
        alignToBusinessDay: { activated: false, periodEndMethod: 'first' },
      },
      rebalance: {
        activated: false,
        methodology: 'calendar-based',
        reconFrequency: 'Q',
        firstReconMonth: 3,
        weekOfMonth: 3,
        weekday: 'FRI',
        alignToBusinessDay: { activated: false, periodEndMethod: 'first' },
      },
    },
  },
  {
    id: 'P000000112',
    name: 'Morningstar Emerging Markets',
    officialName: 'Morningstar Emerging Markets Index',
    benchmarkCode: 'MEMMG',
    displayOrder: 5,
    coverageLabel: 'Emerging Markets',
    coverageScope: 97.0,
    countryCount: 23,
    inception: 'July 31, 1998',
    assetClass: 'Equities',
    assetClassCoverage: 100.0,
    marketSegments: 'All Caps',
    indexReconFrequency: 'Semi Annually',
    indexRebalanceFrequency: 'Quarterly',
    description:
      'The Morningstar Emerging Markets Index measures the performance of ' +
      'large-, mid- and small-cap stocks in emerging markets around the ' +
      'world, representing the top 97% of the investable universe by market ' +
      'capitalization.',
    rebalancer: {
      exchange: 'XNYS',
      holidayRoll: 'following',
      tieBreak: 'previous',
      reconstitution: {
        reconFrequency: 'SA',
        firstReconMonth: 6,
        weekOfMonth: 3,
        weekday: 'FRI',
        alignToBusinessDay: { activated: false, periodEndMethod: 'first' },
      },
      rebalance: {
        activated: false,
        methodology: 'calendar-based',
        reconFrequency: 'Q',
        firstReconMonth: 3,
        weekOfMonth: 3,
        weekday: 'FRI',
        alignToBusinessDay: { activated: false, periodEndMethod: 'first' },
      },
    },
  },
  {
    id: 'P000000050',
    name: 'Morningstar Global Markets',
    officialName: 'Morningstar Global Markets Index',
    benchmarkCode: 'MSGMUSDG',
    displayOrder: 6,
    coverageLabel: 'Global Markets',
    coverageScope: 97.0,
    countryCount: 47,
    inception: 'July 31, 1998',
    assetClass: 'Equities',
    assetClassCoverage: 100.0,
    marketSegments: 'All Caps',
    indexReconFrequency: 'Semi Annually',
    indexRebalanceFrequency: 'Quarterly',
    description:
      'The Morningstar Global Markets Index measures the performance of ' +
      'large-, mid- and small-cap stocks in developed and emerging markets ' +
      'around the world, representing the top 97% of the investable ' +
      'universe by market capitalization.',
    rebalancer: {
      exchange: 'XNYS',
      holidayRoll: 'previous',
      tieBreak: 'previous',
      reconstitution: {
        reconFrequency: 'A',
        firstReconMonth: 6,
        weekOfMonth: 3,
        weekday: 'FRI',
        alignToBusinessDay: { activated: false, periodEndMethod: 'first' },
      },
      rebalance: {
        activated: false,
        methodology: 'calendar-based',
        reconFrequency: 'Q',
        firstReconMonth: 3,
        weekOfMonth: 3,
        weekday: 'FRI',
        alignToBusinessDay: { activated: false, periodEndMethod: 'first' },
      },
    },
  },
];

function toStaticUniverse(def: StaticUniverseDef): Universe {
  return {
    id: def.id,
    name: def.name,
    displayOrder: def.displayOrder,
    officialName: def.officialName,
    benchmarkCode: def.benchmarkCode,
    // `scope` is the live catalogue's per-constituent bucketing; offline the
    // index's own coverage label IS the honest answer, and it's what every
    // consumer prefers anyway (`coverageLabel || scope`).
    scope: def.coverageLabel,
    coverageLabel: def.coverageLabel,
    geographicScopes: UNIVERSE_SCOPES[def.id] ?? [],
    inception: def.inception,
    dataProvider: 'Morningstar',
    coverageScope: def.coverageScope,
    assetClass: {
      label: def.assetClass,
      coverage: def.assetClassCoverage,
    },
    marketSegments: def.marketSegments,
    indexReconFrequency: def.indexReconFrequency,
    indexRebalanceFrequency: def.indexRebalanceFrequency,
    description: def.description,
    rebalancerDetails: def.rebalancer,
    // MEASURED fields — these genuinely come from ClickHouse. Left empty on
    // purpose so the UI can tell "not measured yet" from a real zero.
    marketCap: 0,
    sizeSegments: [],
  };
}

/** The index a fresh form starts on. Matches `buildDraftBacktestConfig`. */
export const DEFAULT_UNIVERSE_ID = '1911833.0';

/**
 * Country coverage per index, shown until the live (filter-aware) count
 * arrives. Like coverage %, this is a published property of the index rather
 * than a measurement, so rendering it immediately isn't a guess — a
 * single-country index covers one country whether or not ClickHouse answered.
 * The live count supersedes it as soon as the securities query resolves, and
 * only then does it reflect the user's own exclusions.
 */
export const STATIC_COUNTRY_COUNTS: Readonly<Record<string, number>> =
  Object.fromEntries(STATIC_DEFS.map((d) => [d.id, d.countryCount]));

/**
 * Legacy display names this file used before the catalogue was keyed by id.
 * ONLY consumed by the v2→v3 persist migration.
 */
const LEGACY_SHORT_NAMES: Record<string, string> = {
  'us target market exposure': '1911833.0',
  'us market extended': '2499059.0',
  'canada domestic': '2575855.0',
  'developed markets (ex-na)': 'P000001109',
  'emerging markets': 'P000000112',
  'morningstar global markets': 'P000000050',
};

/**
 * Map a persisted DISPLAY name — in any vocabulary that has been stored over
 * time — back to its portfolio id. ONLY for migrating persisted state; live
 * code keys on the id.
 *
 * Several vocabularies exist because the API's `name` is ClickHouse's
 * `portfolio_name` when CH is up and the published `official_name` when it is
 * down, while this file previously used its own short form.
 */
export function resolveUniverseId(nameOrId: string): string | null {
  const needle = nameOrId.trim().toLowerCase();
  for (const d of STATIC_DEFS) {
    if (
      d.id.toLowerCase() === needle ||
      d.name.toLowerCase() === needle ||
      d.officialName.toLowerCase() === needle
    ) {
      return d.id;
    }
  }
  return LEGACY_SHORT_NAMES[needle] ?? null;
}

export const STATIC_UNIVERSES: Array<Universe> = STATIC_DEFS.map(toStaticUniverse);

// Last-resort selection fallback, so `selectedUniverse` is never undefined and
// the form stays buildable. Pinned to DEFAULT_UNIVERSE_ID rather than
// STATIC_DEFS[0] so it can't silently change if the catalogue is reordered.
export const FALLBACK_UNIVERSE: Universe = toStaticUniverse(
  STATIC_DEFS.find((d) => d.id === DEFAULT_UNIVERSE_ID) ?? STATIC_DEFS[0],
);
